import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AuctioneerDataMatrixTable from './AuctioneerDataMatrixTable';
import { auctioneerDataMatrixItemsType, auctioneerDataTemplatesType, submarketsType, reducedMembersType } from '../../types';
import SelectField from '../fields/SelectField';
import ResetFieldButton from '../buttons/ResetFieldButton';
import './_styles_/AuctioneerDataMatrix.css';
import NumberField from '../fields/NumberField';
import useSingleValidator from '../../hooks/useSingleValidator';
import { auctioneerDataMatrixQuantityValidator } from '../../validators/auctioneerDataMatrix';
import useExtendedState from '../../hooks/useExtendedState';
import Constants from '../../utils/Constants';
import { hasPrivilege } from '../../hocs/withPrivileges';
import useAuthentication from '../../hooks/useAuthentication';
import { auctionAuctioneerDataTemplateItemsValidator } from '../../validators/auctions';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import useEffectOnLoad from '../../hooks/useEffectOnLoad';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

AuctioneerDataMatrix.propTypes = {
    availableAuctioneers: reducedMembersType.isRequired,
    availableSubmarkets: submarketsType.isRequired,
    quantityMultiple: PropTypes.number,
    maxQuantitySum: PropTypes.number.isRequired,
    items: auctioneerDataMatrixItemsType.isRequired,
    validator: PropTypes.func.isRequired,
    onItemsChange: PropTypes.func.isRequired,
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    isRequired: PropTypes.bool,
    sufficientPrivileges: PropTypes.arrayOf(PropTypes.string),
    availableTemplates: auctioneerDataTemplatesType,
    validateOnLoad: PropTypes.bool,
    doResetComponentsAfterModification: PropTypes.bool,
    doAdjustValuesAfterExternalUpdate: PropTypes.bool,
};

function AuctioneerDataMatrix(props) { // NOSONAR javascript:S3776
    const {
        availableAuctioneers,
        availableSubmarkets,
        availableTemplates,
        quantityMultiple,
        maxQuantitySum,
        items,
        validator,
        onItemsChange,
        onReset = null,
        isChanged = false,
        isRequired = false,
        sufficientPrivileges = null,
        validateOnLoad = false,
        doResetComponentsAfterModification = false,
        doAdjustValuesAfterExternalUpdate = false,
    } = props;
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const isModifiable = sufficientPrivileges === null || hasPrivilege(userPrivileges, sufficientPrivileges);
    const memberIdToAddState = useExtendedState('');
    const submarketCodeToAddState = useExtendedState('');
    const quantityToAddState = useExtendedState(null);
    const templateIdToApplyState = useExtendedState('');
    const [validationWarning, setValidationWarning] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [adjustValuesAfterTemplateApplied, setAdjustValuesAfterTemplateApplied] = useState(false);
    const {
        validator: quantityValidator,
        isValid: isQuantityValid,
        reset: resetQuantityValidator,
    } = useSingleValidator(false, useMemo(() => compositeValidator(auctioneerDataMatrixQuantityValidator(t, Constants.MIN_AUCTIONEER_DATA_QUANTITY, maxQuantitySum, quantityMultiple), wafValidator(t)), [t, maxQuantitySum, quantityMultiple]));
    const {
        validator: selectedTemplateValidator,
        reset: resetSelectedTemplateValidator,
    } = useSingleValidator(true, useMemo(() => auctionAuctioneerDataTemplateItemsValidator(t, maxQuantitySum), [t, maxQuantitySum]));
    const validateSelectedTemplate = (templateIdForValidation) => {
        if (templateIdForValidation) {
            const templateIdForValidationInt = parseInt(templateIdForValidation, 10);
            const templateForValidation = availableTemplates.find((availableTemplate) => availableTemplate.id === templateIdForValidationInt);
            selectedTemplateValidator(templateForValidation.templateItems)
                .then(() => setValidationWarning(null))
                .catch((e) => setValidationWarning(e));
        } else {
            resetSelectedTemplateValidator();
            setValidationWarning(null);
        }
    };
    const [doReset, setDoReset] = useState(false);
    const onResetInternal = () => {
        setDoReset(true);
        onReset();
    };
    const changeItems = (newItems) => {
        validator(newItems)
            .then(() => setValidationError(null))
            .catch((e) => setValidationError(e));
        onItemsChange(newItems);
        setAdjustValuesAfterTemplateApplied(false);
        setDoReset(false);
    };
    useEffectOnUpdate(() => {
        validator(items)
            .then(() => setValidationError(null))
            .catch((e) => setValidationError(e));
        validateSelectedTemplate(templateIdToApplyState.value);
        // "validator" is intentionally excluded from dependency array of useEffect() because it's recreated on render, so it would end up in endless loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, maxQuantitySum]);
    useEffectOnLoad(() => {
        if (validateOnLoad) {
            validator(items)
                .then(() => setValidationError(null))
                .catch((e) => setValidationError(e));
            validateSelectedTemplate(templateIdToApplyState.value);
        }
    });
    const addItem = () => {
        changeItems([...items, {
            auctioneer: availableAuctioneers.find((availableAuctioneer) => availableAuctioneer.memberId === memberIdToAddState.value),
            submarket: availableSubmarkets.find((availableSubmarket) => availableSubmarket.code === submarketCodeToAddState.value),
            quantity: quantityToAddState.value,
        }]);
        memberIdToAddState.reset();
        submarketCodeToAddState.reset();
        quantityToAddState.reset();
        resetQuantityValidator();
    };
    const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(true);
    isQuantityValid()
        .then(() => setIsAddButtonDisabled(!memberIdToAddState.value || !submarketCodeToAddState.value))
        .catch(() => setIsAddButtonDisabled(true));

    const isApplyTemplateButtonDisabled = !templateIdToApplyState.value;
    const removeItem = (itemToRemove) => () => {
        changeItems(_.without(items, itemToRemove));
    };
    const changeQuantity = (itemToChange) => (newQuantity) => {
        const newItems = items.map((item) => {
            let result = item;
            if (item === itemToChange) {
                result = { ...item, quantity: newQuantity };
            }
            return result;
        });
        changeItems(newItems);
    };
    const onNewTemplateSelected = (newTemplateIdToApply) => {
        validateSelectedTemplate(newTemplateIdToApply);
    };
    const applyTemplate = () => {
        const templateIdToApplyInt = parseInt(templateIdToApplyState.value, 10);
        const templateToApply = availableTemplates.find((availableTemplate) => availableTemplate.id === templateIdToApplyInt);
        changeItems([...templateToApply.templateItems]);
        memberIdToAddState.reset();
        submarketCodeToAddState.reset();
        quantityToAddState.reset();
        templateIdToApplyState.reset();
        resetQuantityValidator();
        setAdjustValuesAfterTemplateApplied(true);
    };
    const groupedItems = _.groupBy(items, 'auctioneer.memberId');
    const groupedMemberIdsSubmarketCodes = _.mapValues(
        groupedItems,
        (arrOfTmpItems) => _.map(_.uniqBy(arrOfTmpItems, 'submarket.code'), 'submarket.code'),
    );
    const realAvailableAuctioneers = availableAuctioneers.filter((availableAuctioneer) => (
        !groupedMemberIdsSubmarketCodes[availableAuctioneer.memberId]
            || groupedMemberIdsSubmarketCodes[availableAuctioneer.memberId].length < availableSubmarkets.length
    ));
    const submarketCodesForSelectedMember = groupedMemberIdsSubmarketCodes[memberIdToAddState.value] || [];
    let realAvailableSubmarkets = [];
    if (memberIdToAddState.value) {
        realAvailableSubmarkets = submarketCodesForSelectedMember.length === 0
            ? availableSubmarkets
            : availableSubmarkets.filter((availableSubmarket) => !submarketCodesForSelectedMember.includes(availableSubmarket.code));
    }
    return (
        <div className={`auctioneer-data-matrix field${!isModifiable ? ' readonly' : ''}${isChanged ? ' modified' : ''}${(isRequired && !items.length) ? ' required' : ''}`}>
            <div className="auctioneer-data-matrix-field">
                <div className="field--key">
                    {t('auctioneer-data-matrix.title')}
                    <div className="auctioneer-data-matrix--validations">
                        {validationError && (
                            <div className="validation-error auctioneer-data-matrix--error">
                                {validationError}
                            </div>
                        )}
                        {validationWarning && (
                            <div className="validation-warning auctioneer-data-matrix--warning">
                                {validationWarning}
                            </div>
                        )}
                    </div>
                </div>
                {isModifiable && (
                    <div className="auctioneer-data-matrix--add-items">
                        <div className="auctioneer-data-matrix--add-item">
                            <SelectField
                                label={t('auctioneer-data-matrix.member')}
                                inputId="auctioneer-data-matrix--fields--member"
                                className="auctioneer-data-matrix--fields--member"
                                state={memberIdToAddState}
                                emptyOption={<option value="">...</option>}
                                options={realAvailableAuctioneers.map((availableMember) => (
                                    <option
                                        key={availableMember.memberId}
                                        value={availableMember.memberId}
                                    >
                                        {availableMember.name} ({availableMember.memberId})
                                    </option>
                                ))}
                            />
                            <SelectField
                                label={t('auctioneer-data-matrix.submarket')}
                                inputId="auctioneer-data-matrix--fields--submarket"
                                className="auctioneer-data-matrix--fields--submarket"
                                state={submarketCodeToAddState}
                                emptyOption={<option value="">...</option>}
                                options={realAvailableSubmarkets.map((availableSubmarket) => (
                                    <option
                                        key={availableSubmarket.code}
                                        value={availableSubmarket.code}
                                    >
                                        {availableSubmarket.name} ({availableSubmarket.code})
                                    </option>
                                ))}
                                disabled={!memberIdToAddState.value}
                            />
                            <NumberField
                                label={t('auctioneer-data-matrix.quantity')}
                                inputId="auctioneer-data-matrix--fields--quantity"
                                className="auctioneer-data-matrix--fields--quantity"
                                validator={quantityValidator}
                                state={quantityToAddState}
                                step={quantityMultiple}
                                min={Constants.MIN_AUCTIONEER_DATA_QUANTITY}
                                max={maxQuantitySum}
                                disabled={!memberIdToAddState.value || !submarketCodeToAddState.value}
                                validateOnValidatorChange
                                roundToStepMultiple
                            />
                            <button
                                type="button"
                                className="add-item-button button button-bordered"
                                disabled={isAddButtonDisabled}
                                onClick={addItem}
                            >
                                <FontAwesomeIcon icon="user-plus" />&nbsp;{t('auctioneer-data-matrix.add-item')}
                            </button>
                        </div>
                        {availableTemplates && availableTemplates.length > 0 && (
                            <div className="auctioneer-data-matrix--apply-template">
                                <SelectField
                                    label={t('auctioneer-data-matrix.template')}
                                    inputId="auctioneer-data-matrix--fields--template"
                                    className="auctioneer-data-matrix--fields--template"
                                    onChange={onNewTemplateSelected}
                                    emptyOption={<option value="">...</option>}
                                    state={templateIdToApplyState}
                                    options={availableTemplates.map((availableTemplate) => (
                                        <option
                                            key={availableTemplate.id}
                                            value={availableTemplate.id}
                                        >
                                            {availableTemplate.name}
                                        </option>
                                    ))}
                                />
                                <button
                                    type="button"
                                    className="apply-template-button button button-bordered"
                                    disabled={isApplyTemplateButtonDisabled}
                                    onClick={applyTemplate}
                                >
                                    <FontAwesomeIcon icon="check" />&nbsp;{t('auctioneer-data-matrix.apply-template')}
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {items.length > 0 && (
                    <AuctioneerDataMatrixTable
                        items={items}
                        removeItem={removeItem}
                        changeQuantity={changeQuantity}
                        quantityMultiple={quantityMultiple}
                        maxQuantity={maxQuantitySum}
                        isModifiable={isModifiable}
                        doReset={doReset}
                        doResetComponentsAfterModification={doResetComponentsAfterModification}
                        doAdjustValuesAfterTemplateApplied={adjustValuesAfterTemplateApplied}
                        doAdjustValuesAfterExternalUpdate={doAdjustValuesAfterExternalUpdate}
                    />
                )}
            </div>
            {isModifiable && isChanged && onReset && <ResetFieldButton onReset={onResetInternal} />}
        </div>
    );
}

export default AuctioneerDataMatrix;
